import { takeLatest, put, call } from "redux-saga/effects";
import {
    fetchPromptsStart,
    fetchPromptsSuccess,
    fetchPromptsFail,
    fetchPromptDetailsStart,
    fetchPromptDetailsSuccess,
    fetchPromptDetailsFail,
    fetchRandomPromptIDStart,
    fetchRandomPromptIDSuccess,
    fetchRandomPromptIDFail,
    fetchCategoriesStart,
    fetchCategoriesSuccess,
    fetchCategoriesFail,
    fetchDataModalsStart,
    fetchDataModalsSuccess,
    fetchDataModalsFail,
    favouritePromptStart,
    favouritePromptSuccess,
    favouritePromptFail,
    reactToDataModalStart,
    reactToDataModalSuccess,
    reactToDataModalFail,
    fetchSeenPromptsStart,
    fetchSeenPromptsSuccess,
    fetchSeenPromptsFail,
    fetchSeenPromptIdsStart,
    fetchSeenPromptIdsSuccess,
    fetchSeenPromptIdsFail,

    fetchUnseenPromptsStart,
    fetchUnseenPromptsSuccess,
    fetchUnseenPromptsFail,

    fetchFavouritePromptsStart,
    fetchFavouritePromptsSuccess,
    fetchFavouritePromptsFail,
    fetchPromptsSummarySuccess,
    fetchPromptsSummaryFail,
    fetchPromptsSummaryStart,
    setGuestQuotaExceeded,
    checkUserFeedbackStart,
    checkUserFeedbackSuccess,
    checkUserFeedbackFail,
} from "../slices/promptSlice";
import { getRequest, postRequest } from "../../network";
import { PROMPTS, PROMPT_DETAILS, PROMPT_CATEGORIES, PROMPT_DATA_MODALS, REACT_DATA_MODALS, FAVOURITE_PROMPT, UNSEEN_PROMPTS, SEEN_PROMPTS, SEEN_PROMPT_IDS, FAVOURITE_PROMPTS, PROMPTS_SUMMARY, CHECK_USER_FEEDBACK, GET_RANDOM_PROMPT_ID } from "../apis";

// Fetch All Prompts
function* fetchPromptsSaga(action) {
    try {
        const response = yield call(getRequest, PROMPTS, action.payload);
        yield put(fetchPromptsSuccess(response));
    } catch (error) {
        yield put(fetchPromptsFail(error.message));
    }
}

// Fetch Random Prompt ID
function* fetchRandomPromptIDSaga() {
    try {
        const response = yield call(getRequest, GET_RANDOM_PROMPT_ID);
        yield put(fetchRandomPromptIDSuccess(response));
    } catch (error) {
        yield put(fetchRandomPromptIDFail(error.response?.data?.message || error.message));
    }
}

// Fetch Single Prompt Details
function* fetchPromptDetailsSaga(action) {
    try {
        const { promptID, personalityCombination } = action.payload;
        const response = yield call(getRequest, PROMPT_DETAILS.replace(':promptID', promptID), { personalityCombination });
        yield put(fetchPromptDetailsSuccess(response));
    } catch (error) {
        // Handle 403 quota exceeded response
        if (error.response?.status === 403 && error.response?.data?.quotaExceeded) {
            yield put(setGuestQuotaExceeded({
                genericPromptsViewed: error.response.data.genericPromptsViewed,
                limit: error.response.data.limit
            }));
            yield put(fetchPromptDetailsFail(error.response?.data?.message || "Guest prompt limit reached. Please log in to explore more."));
            return;
        } else if (error.response?.status === 403 && error.response?.data?.requiresAssessment) {
            yield put(fetchPromptDetailsFail("Complete your assessment to unlock personalized prompts."));
            return;
        }
        yield put(fetchPromptDetailsFail(error.response?.data?.message || error.message));
    }
}

// Fetch Categories
function* fetchCategoriesSaga(action) {
    try {
        const response = yield call(getRequest, PROMPT_CATEGORIES, action.payload);
        yield put(fetchCategoriesSuccess(response));
    } catch (error) {
        yield put(fetchCategoriesFail(error.message));
    }
}

// Fetch Data Modals
function* fetchDataModalsSaga(action) {
    try {
        const response = yield call(getRequest, PROMPT_DATA_MODALS, action.payload);
        yield put(fetchDataModalsSuccess(response));
    } catch (error) {
        yield put(fetchDataModalsFail(error.message));
    }
}

function* reactToDataModalSaga(action) {
    try {
        const { dataModalID, reaction } = action.payload; // reaction - 'like' | 'dislike' | null
        yield call(postRequest, REACT_DATA_MODALS.replace(':id', dataModalID), { reaction });
        yield put(reactToDataModalSuccess({ dataModalID, reaction }));
    } catch (error) {
        yield put(reactToDataModalFail(error.message));
    }
}

function* favPromptSaga(action) {
    try {
        const { promptID, favourite } = action.payload; // favourite - true | false
        const response = yield call(postRequest, FAVOURITE_PROMPT.replace(':promptID', promptID), { favourite });
        yield put(favouritePromptSuccess(response));
    } catch (error) {
        yield put(favouritePromptFail(error.message));
    }
}

// 👁 Seen Prompts
function* seenPromptsSaga(action) {
    try {
        const response = yield call(getRequest, SEEN_PROMPTS, action.payload);
        yield put(fetchSeenPromptsSuccess(response));
    } catch (error) {
        yield put(fetchSeenPromptsFail(error.message));
    }
}

function* seenPromptIdsSaga() {
    try {
        const response = yield call(getRequest, SEEN_PROMPT_IDS);
        yield put(fetchSeenPromptIdsSuccess(response));
    } catch (error) {
        yield put(fetchSeenPromptIdsFail(error.message));
    }
}

// 🚫 Unseen Prompts
function* unseenPromptsSaga(action) {
    try {
        const response = yield call(getRequest, UNSEEN_PROMPTS, action.payload);
        yield put(fetchUnseenPromptsSuccess(response));
    } catch (error) {
        yield put(fetchUnseenPromptsFail(error.message));
    }
}

// ⭐️ Favourite Prompts
function* favouritePromptsSaga(action) {
    try {
        const response = yield call(getRequest, FAVOURITE_PROMPTS, action.payload);
        yield put(fetchFavouritePromptsSuccess(response));
    } catch (error) {
        yield put(fetchFavouritePromptsFail(error.message));
    }
}

// 👁 Seen Prompts
function* promptsSummarySaga() {
    try {
        const response = yield call(getRequest, PROMPTS_SUMMARY);
        yield put(fetchPromptsSummarySuccess(response));
    } catch (error) {
        yield put(fetchPromptsSummaryFail(error.message));
    }
}

// Check if user has given feedback
function* checkUserFeedbackSaga() {
    try {
        const response = yield call(getRequest, CHECK_USER_FEEDBACK);
        yield put(checkUserFeedbackSuccess(response));
    } catch (error) {
        yield put(checkUserFeedbackFail());
    }
}

export function* watchPrompt() {
    yield takeLatest(fetchPromptsStart.type, fetchPromptsSaga);
    yield takeLatest(fetchPromptDetailsStart.type, fetchPromptDetailsSaga);
    yield takeLatest(fetchRandomPromptIDStart.type, fetchRandomPromptIDSaga);
    yield takeLatest(fetchCategoriesStart.type, fetchCategoriesSaga);
    yield takeLatest(fetchDataModalsStart.type, fetchDataModalsSaga);
    yield takeLatest(reactToDataModalStart.type, reactToDataModalSaga);
    yield takeLatest(favouritePromptStart.type, favPromptSaga);
    yield takeLatest(fetchSeenPromptsStart.type, seenPromptsSaga);
    yield takeLatest(fetchSeenPromptIdsStart.type, seenPromptIdsSaga);
    yield takeLatest(fetchUnseenPromptsStart.type, unseenPromptsSaga);
    yield takeLatest(fetchFavouritePromptsStart.type, favouritePromptsSaga);
    yield takeLatest(fetchPromptsSummaryStart.type, promptsSummarySaga);
    yield takeLatest(checkUserFeedbackStart.type, checkUserFeedbackSaga);
}